<template>
  <div
    class="eg-table-base-filters"
    :class="`filters-size-${filtersSize}`"
    :data-testid="dataTestid"
  >
    <div class="filters d-flex align-items-center flex-wrap">
      <!-- Filter dropdowns -->
      <div
        v-for="(filter, index) in selectedFilters"
        :key="filter.key"
        :data-testid="`filter-dropdown-${filter.key}`"
      >
        <filter-dropdown
          ref="filter-dropdown"
          :button-label="
            allAvailableFilters[filter.key]?.filterName ||
            allAvailableFilters[filter.key]?.title
          "
          :active="filter.isActive"
          :selected="filter.value"
          :right="index > rightDropdownIndex"
          :title="allAvailableFilters[filter.key].title"
          :delete-label="getDeleteLabel(filter)"
          @apply="applyFilter(index)"
          @delete="deleteFilter(index)"
        >
          <component
            :is="allAvailableFilters[filter.key].component"
            v-model="filter.value"
            :rubrics="rubrics"
            :items="allAvailableFilters[filter.key].items"
            :placeholder="
              allAvailableFilters[filter.key]?.placeholder ||
              allAvailableFilters[filter.key].title
            "
            :settings="allAvailableFilters[filter.key]?.settings"
            :is-clearable="allAvailableFilters[filter.key]?.isClearable"
            value-is-id
            no-label
            @keypress.enter="applyFilter(index)"
          />
        </filter-dropdown>
      </div>
    </div>

    <!-- Add filter dropdown -->
    <template v-if="showAddFilter && notShowedAvailableFilters.length">
      <div v-if="selectedFilters.length" class="divider m-0 ml-2" />
      <eg-b-dropdown
        class="eg-filters-selector-dropdown"
        no-caret
        variant="link"
        size="sm"
      >
        <template #button-content>
          <div class="text-info">+ {{ $t("common.addFilter") }}</div>
        </template>
        <eg-b-dropdown-item
          v-for="filterKey in notShowedAvailableFilters"
          :key="filterKey"
          :value="filterKey"
          @click="addFilter(filterKey)"
        >
          {{
            allAvailableFilters[filterKey]?.filterName ||
            allAvailableFilters[filterKey]?.title
          }}
        </eg-b-dropdown-item>
      </eg-b-dropdown>
    </template>

    <!-- Clear all button -->
    <template v-if="showClearAll && isFiltered">
      <div class="divider" />
      <eg-b-button variant="link" class="clear-all px-1 ml-2" @click="clearAll">
        {{ $t("common.clearAll") }}
      </eg-b-button>
    </template>

    <!-- Search slot or default eg-search -->
    <slot v-if="showSearch" name="search">
      <eg-input
        ref="searchInput"
        v-model="searchValue"
        class="ml-auto"
        :placeholder="$t('common.search')"
        variant="search"
        size="small"
        @onChange="handleSearch"
      />
    </slot>

    <!-- Sort dropdown -->
    <div v-if="showSort" class="sort">
      <table-sort-dropdown
        :available-sort="availableSorts"
        @sort="$emit('sort-changed', $event)"
      />
    </div>

    <!-- Additional slot for custom content -->
    <slot name="additional" />

    <!-- Essay-specific slots -->
    <slot name="essay-actions" />
    <slot name="select-all" />
  </div>
</template>

<script>
import debounce from "@/utils/debounce";
import FilterDropdown from "@/components/essay/filters/filter_dropdown.vue";
import TableSortDropdown from "@/components/essay/filters/table_sort_dropdown.vue";
import FilterSelect from "@/components/filter-select";
import InputFilter from "@/components/essay/filters/input_filter.vue";
import DateRange from "@/components/essay/filters/date_range.vue";
import RubricSelect from "@/components/rubrics/rubric_select";
import ClassSelect from "@/components/classes/class_select";
import StudentSelect from "@/components/students/student_select.vue";
import AssignmentSelect from "@/components/essay/assignment_select";
import GradeRange from "@/components/grade_range";
import EssayActions from "@/components/essay/filters/essay_actions.vue";

export default {
  name: "EgTableFiltersBase",

  components: {
    FilterDropdown,
    TableSortDropdown,
    FilterSelect,
    InputFilter,
    DateRange,
    RubricSelect,
    ClassSelect,
    StudentSelect,
    AssignmentSelect,
    GradeRange,
    EssayActions,
  },

  props: {
    // Filters configuration
    availableFilters: {
      type: Array,
      required: true,
    },
    allAvailableFilters: {
      type: Object,
      required: true,
    },
    defaultSelectedFilters: {
      type: Array,
      default: () => [],
    },

    // Feature toggles
    showAddFilter: {
      type: Boolean,
      default: true,
    },
    canRemoveFilters: {
      type: Boolean,
      default: true,
    },
    showClearAll: {
      type: Boolean,
      default: true,
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
    showSort: {
      type: Boolean,
      default: false,
    },
    filtersSize: {
      type: String,
      required: false,
      default: "small",
    },

    // Sort configuration
    availableSorts: {
      type: Array,
      default: () => ["name", "createdAt"],
    },

    // Additional data
    rubrics: {
      type: Array,
      default: () => [],
    },

    dataTestid: {
      type: String,
      default: "eg-table-base-filters",
    },

    // Dropdown positioning
    rightDropdownIndex: {
      type: Number,
      default: 3,
    },

    // Essay-specific props
    showSelectAll: {
      type: Boolean,
      default: false,
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
    selectedAll: {
      type: Boolean,
      default: false,
    },
    total: {
      type: Number,
      default: 0,
    },
    limit: {
      type: Number,
      default: 0,
    },
    itemsToSelectCounts: {
      type: Number,
      default: 10000,
    },
    isFiltered: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedFilters: [],
      searchValue: "",
    };
  },

  computed: {
    notShowedAvailableFilters() {
      const selectedFiltersKeys = this.selectedFilters.map(
        (filter) => filter.key
      );

      return this.availableFilters.filter(
        (filter) => !selectedFiltersKeys.includes(filter)
      );
    },
  },

  created() {
    // Initialize with default selected filters or first available filter
    if (this.defaultSelectedFilters.length > 0) {
      this.selectedFilters = this.defaultSelectedFilters.map((filter) => ({
        key: filter.key || filter,
        value: filter.value || null,
        isActive: filter.isActive || false,
      }));
    } else if (this.availableFilters.length > 0 && this.showAddFilter) {
      // Only add default filter if we can add/remove filters
      this.selectedFilters = [
        { key: this.availableFilters[0], value: null, isActive: false },
      ];
    }
  },

  methods: {
    handleSearch: debounce(function (value) {
      this.searchValue = value;
      // Collect active filters from dropdowns
      const activeFilters = this.getActiveFilters();

      // Merge search filter with active filters
      this.$emit("filter-changed", {
        filters: {
          ...activeFilters,
          name: value,
        },
      });
    }, 300),

    getActiveFilters() {
      return this.selectedFilters
        .filter((filter) => filter.isActive && filter.value !== null)
        .reduce((acc, filter) => {
          const filterKey = this.allAvailableFilters[filter.key].key;
          if (!filterKey) {
            console.log("No filter key for", filter.key);
            return acc;
          }

          if (Array.isArray(filterKey)) {
            filterKey.forEach((key) => {
              acc[key] = filter.value[key];
            });
            return acc;
          }

          const value = filter?.value?.id ? filter.value.id : filter.value;
          if (value !== null && value !== undefined) {
            acc[filterKey] = value;
          }
          return acc;
        }, {});
    },

    emitFilters() {
      const filters = this.getActiveFilters();
      // Include search value if it exists
      if (this.searchValue) {
        filters.name = this.searchValue;
      }
      this.$emit("filter-changed", { filters });
    },

    emitFiltersWithCleared(clearedFilterKey) {
      const filters = this.getActiveFilters();

      // Add the cleared filter with null to explicitly remove it
      if (clearedFilterKey) {
        if (Array.isArray(clearedFilterKey)) {
          clearedFilterKey.forEach((key) => {
            filters[key] = null;
          });
        } else {
          filters[clearedFilterKey] = null;
        }
      }

      // Include search value if it exists
      if (this.searchValue) {
        filters.name = this.searchValue;
      }

      this.$emit("filter-changed", { filters });
    },

    applyFilter(index) {
      this.selectedFilters[index].isActive = true;
      this.emitFilters();
      this.$refs["filter-dropdown"]?.[index]?.closeDropdown();
    },

    addFilter(filterKey) {
      this.selectedFilters.push({
        key: filterKey,
        value: this.allAvailableFilters[filterKey]?.defaultValue || null,
        isActive: false,
      });

      this.$nextTick(() => {
        this.$refs["filter-dropdown"][
          this.selectedFilters.length - 1
        ]?.openDropdown();
      });
    },

    deleteFilter(index) {
      const isActive = this.selectedFilters[index].isActive;
      const filters = this.selectedFilters[index];

      if (this.canRemoveFilters) {
        // Remove filter from list
        this.selectedFilters.splice(index, 1);

        if (isActive) {
          this.emitFilters();
        }
      } else {
        // Clear filter value instead of removing it
        filters.isActive = false;
        filters.value = null;

        if (isActive) {
          this.emitFiltersWithCleared(filters.key);
        }
      }
    },

    clearAll() {
      this.selectedFilters.forEach((filter) => {
        filter.isActive = false;
        filter.value = null;
      });
      this.searchValue = "";

      // Clear the search input component directly to ensure DOM updates
      this.$nextTick(() => {
        if (this.$refs.searchInput && this.$refs.searchInput.clearValue) {
          this.$refs.searchInput.clearValue();
        }
      });

      this.$emit("filter-changed", { filters: { name: "" } });
    },

    getDeleteLabel(filter) {
      const isClearable = this.allAvailableFilters[filter.key]?.isClearable;
      const hasValue = filter.value?.length || filter.value;

      if (isClearable && hasValue) {
        return this.$t("common.clearAll");
      }

      return this.canRemoveFilters
        ? this.$t("common.deleteFilter")
        : this.$t("common.clearFilter");
    },

    toggleSelectedAll() {
      this.$emit("selectedAll", !this.selectedAll);
    },
  },
};
</script>

<style lang="scss" scoped>
.filters-size-medium {
  ::v-deep {
    .essay-actions-dropdown > .btn {
      line-height: 18px;
    }
    .eg-input_size-small:not(.eg-input_variant-textarea) .eg-input__control {
      height: 36px;
    }
  }
}

.selected-text {
  border: 1px solid transparent;
}

.clear-all {
  color: #6c757d;
  flex: 0 1 75px;
  font-size: 14px;
  font-weight: 400;
  line-height: 17px;
  flex-shrink: 0;
}

.filters {
  gap: 8px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
}

.text-info:focus {
  box-shadow: none;
  text-decoration: none;
}

.divider {
  margin: 0 8px;
  height: 31px;
  border-left: 1px solid var(--secondary);
}
</style>
